import { parseDateTime } from "@internationalized/date";

/**
 * Checks if two log entries occurred on the same day.
 * It compares the year, month, and day of the 'time' property of both log objects.
 */
const _areSameDay = (log1, log2) => {
  const date1 = new Date(log1.time);
  const date2 = new Date(log2.time);
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * Determines if two log entries are close in time based on a specified aggregation threshold.
 * Checks if both logs occurred on the same day and if the time difference between them
 * is less than or equal to the provided aggregation value (in seconds).
 */
export const _areClose = (log1, log2, aggregation) => {
  if (log1 === undefined || log2 === undefined) return false;
  const date1 = new Date(log1.time);
  const date2 = new Date(log2.time);
  return _areSameDay(log1, log2) && Math.abs((date2 - date1) / 1000) <= aggregation;
};

export const convertTimeToSeconds = (timeStr) => {
  const parts = timeStr.split(':').map(Number);
  let seconds = 0;

  if (parts.length === 3) {
    seconds = parts[0] * 3600 + parts[1] * 60 + parts[2];
  } else if (parts.length === 2) {
    seconds = parts[0] * 60 + parts[1];
  }

  return seconds;
};

export const formatSecondsDuration = (seconds) => {
  if (seconds < 60) {
    return seconds + 's';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  let result = '';
  if (hours > 0) {
    result += hours + 'h';
  }
  if (minutes > 0) {
    if (result !== '') {
      result += ' ';
    }
    result += minutes + 'min';
  }
  return result;
};

export const formatSecondsToMinutesSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${secs}`;
};

export const convertSmallUnitToSeconds = (timeString) => {
  const units = {
    s: 1,
    m: 60,
    h: 3600,
    d: 86400,
  };

  const regex = /(\d+)\s*([smhd]?)/g;
  let match;
  let totalSeconds = 0;

  while ((match = regex.exec(timeString)) !== null) {
    const value = parseInt(match[1]);
    const unit = match[2] || 's'; // Default unit is seconds if not specified

    totalSeconds += value * units[unit];
  }

  return totalSeconds;
};

export const convertDateToTimestamp = (dateString) => {
  const [datePart, timePart] = dateString.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  const date = new Date(year, month - 1, day, hours, minutes);
  const timestamp = date.getTime() / 1000;
  return timestamp;
};

export const convertIsoDateToTimestamp = (isoDateString) => {
  if (typeof isoDateString !== 'string') {
    throw new Error('Date must be a string in ISO 8601 format.');
  }

  const date = new Date(isoDateString);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid date.');
  }

  return date.getTime();
};

export const convertTimestampToIsoDate = (timestamp) => {
  if (typeof timestamp !== 'number') {
    throw new Error('Timestamp must be a number representing milliseconds.');
  }

  const date = new Date(timestamp);

  if (isNaN(date.getTime())) {
    throw new Error('Invalid timestamp.');
  }

  return date.toISOString();
};

/**
 * Get string date with custom separator beginning by year (ex: yyyy-mm-dd-hh-mm-ss)
 * @param timestamp
 * @param separator
 * @returns {string}
 */
export const getCustomFormattedDateBeginningByYear = (timestamp, separator = '-') => {
  timestamp = new Date(timestamp * 1000);
  let day = timestamp.getDate() <= 9 ? '0' + timestamp.getDate() : timestamp.getDate();
  let month = timestamp.getMonth() >= 9 ? timestamp.getMonth() + 1 : '0' + (timestamp.getMonth() + 1);
  let hour = timestamp.getHours() <= 9 ? '0' + timestamp.getHours() : timestamp.getHours();
  let minutes = timestamp.getMinutes() <= 9 ? '0' + timestamp.getMinutes() : timestamp.getMinutes();
  let seconds = timestamp.getSeconds() <= 9 ? '0' + timestamp.getSeconds() : timestamp.getSeconds();

  return [timestamp.getFullYear(), month, day, hour, minutes, seconds].join(separator);
}

export const getCurrentTimestamp = () => {
  return Math.floor(Date.now() / 1000);
}

export const formatSecondsToHoursMinutesSeconds = (ts) => {
  const date = new Date(ts * 1000);
  return date.toTimeString().split(' ')[0];
};

export const formatSecondsToFullDate = (ts) => {
  const date = new Date(ts * 1000);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatSecondsToFullDateTime = (ts, withSeconds=true) => {
  const date = new Date(ts * 1000);

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}${withSeconds ? `:${seconds}` : ''}`;
};

export const formatDateTimeFromTimestamp = (ts) => {
  const date = new Date(ts);
  const pad = (n) => n.toString().padStart(2, "0");

  const isoLocal = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

  return parseDateTime(isoLocal);
};

export const calendarDateTimetoDate = (calendarDateTime) => {
  if (!calendarDateTime) return null;

  const {
    year,
    month,
    day,
    hour = 0,
    minute = 0,
    second = 0,
  } = calendarDateTime;

  return new Date(year, month - 1, day, hour, minute, second);
};

export const formatSecondsToHMS = (s, format='long', includeSeconds=true) => {
  s = Math.ceil(s);
  if (s === 0) return format === 'long' ? '0s' : '0';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (format === 'long')
    return `${h > 0 ? `${h}h ` : ""}${m > 0 ? `${m}min ` : ""}${sec > 0 && includeSeconds ? sec.toString().padStart(2, "0") + "s" : ""}`;
  else
    return `${h > 0 ? `${h}'` : ""}${m > 0 ? `${m}'` : ""}${sec > 0 && includeSeconds ? sec.toString().padStart(2, "0") : ""}`;
};